<script lang="ts">
	import { format } from '../../../services/i18n';
	import {
		resetSceneMainFilter,
		roomsScenesFilter,
		sceneMainFilter,
		getSelectedSceneFilterCount,
	} from 'src/stores/ui/filters';
	import { defaultSelectedRoom } from 'src/helpers/constants';
	import { ComponentType } from '@Shelly/shelly-design-system/helpers/enums';
	import { is_mobile } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';

	import ScenesFilterModal from './ScenesFilterModal.svelte';

	import {
		Toolbar,
		Breadcrumbs,
		IconButton,
		Input,
		Chip,
		Button,
		BreadcrumbsFlexWrapper,
	} from '@Shelly/shelly-design-system';
	import RoomToolbar from '../Rooms/RoomToolbar.svelte';
	import RoomBreadCrumbs from '../Rooms/RoomBreadCrumbs.svelte';

	import { capFirstChar } from '../../../helpers/utilities';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';

	export let type = 'scenes';
	export let id; // the id from route, whatever it is
	export let lazyShowChatModal: () => void;
	export let showAddScriptSceneModal: boolean;

	let openFilterModal = false;
	export let disableReorder: boolean = false;

	$: notification =
		getSelectedSceneFilterCount($roomsScenesFilter) > 0
			? ({
					title: getSelectedSceneFilterCount($roomsScenesFilter),
					type: ComponentType.DANGER,
				} as const)
			: null;

	function resetData() {
		resetSceneMainFilter();
		roomsScenesFilter.set({ rooms: [defaultSelectedRoom] });
	}
</script>

<Toolbar
	class="mb-m"
	isSticky
	useNewLayout
	hideSlotLeftSide={$edit || (!$is_mobile && type !== 'room')}
	hideSlotRightSide={($is_mobile && type !== 'room') || !notification}
	hideSlotBottom={!notification || $edit || ($is_mobile && type !== 'room')}
>
	<svelte:fragment slot="breadcrumbs">
		{#if type === 'room'}
			<RoomBreadCrumbs {id} />
		{:else if $is_mobile && type !== 'room'}
			<BreadcrumbsFlexWrapper gap="s">
				<div slot="left" class="flex-1 min-width-0 max-width-fit-content">
					<Breadcrumbs
						listClasses="width-full"
						links={[{ label: capFirstChar($format('tab_scenes')) }]}
						aria-label={$format('label_scenes', { default: 'Scenes' })}
					/>
				</div>

				<div slot="right">
					{#if isExperimentalEnabled('inf1')}
						<IconButton
							aria-label="Open Scene Creator Assistant"
							class="align-self-center"
							icon="fa-solid fa-microchip-ai"
							variant="secondary"
							disabled={false}
							on:click={lazyShowChatModal}
						/>
					{/if}

					{#if isExperimentalEnabled('inf2') && !$edit}
						<IconButton
							aria-label="Add Script Scene"
							class="align-self-center"
							icon="fa-brands fa-js"
							variant="secondary"
							disabled={false}
							on:click={() => {
								showAddScriptSceneModal = !showAddScriptSceneModal;
							}}
						/>
					{/if}

					{#if notification}
						<Button
							size="m"
							variant="secondary"
							class="align-self-center"
							disabled={disableReorder}
							icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
							on:click={() => {
								resetDrawer();
								edit.set(!$edit);
							}}
						>
							{$edit
								? $format('label_finish_reordering', {
										default: 'Finish reordering',
									})
								: $format('label_reorder_items', { default: 'Reorder items' })}
						</Button>
					{/if}
				</div>
			</BreadcrumbsFlexWrapper>
		{:else if !$is_mobile && type !== 'room'}
			<BreadcrumbsFlexWrapper gap="s">
				<div
					class="d-flex align-items-center flex-1 min-width-0 max-width-fit-content gap-s"
					slot="left"
				>
					<Breadcrumbs
						class="flex-1 min-width-0"
						listClasses="width-full"
						links={[{ label: capFirstChar($format('tab_scenes')) }]}
						aria-label={$format('label_scenes', { default: 'Scenes' })}
					/>

					{#if !$edit}
						<div class="d-flex align-items-center justify-content-center">
							<Input
								class="width-md-l mr-sm-s text-capitalize-placeholder"
								bind:value={$sceneMainFilter}
								type="text"
								label={capFirstChar($format('search', { default: 'Search' }))}
								icon="fas fa-search"
								id="scenes-toolbar-search-input"
								aria-labelledby="scenes-toolbar-search-input"
							/>
							<IconButton
								icon="fa-solid fa-filter"
								on:click={() => {
									openFilterModal = true;
								}}
								aria-label={$format('aria_open_filter', { default: 'Open filter' })}
								{notification}
							/>
						</div>
					{/if}
				</div>

				<div slot="right">
					{#if isExperimentalEnabled('inf1')}
						<IconButton
							aria-label="Open Scene Creator Assistant"
							class="align-self-center"
							icon="fa-solid fa-microchip-ai"
							variant="secondary"
							disabled={false}
							on:click={lazyShowChatModal}
						/>
					{/if}

					{#if isExperimentalEnabled('inf2') && !$edit}
						<IconButton
							aria-label="Add Script Scene"
							class="align-self-center"
							icon="fa-brands fa-js"
							variant="secondary"
							disabled={false}
							on:click={() => {
								showAddScriptSceneModal = !showAddScriptSceneModal;
							}}
						/>
					{/if}

					{#if notification}
						<Button
							size="m"
							variant="secondary"
							class="align-self-center"
							disabled={disableReorder}
							icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
							on:click={() => {
								resetDrawer();
								edit.set(!$edit);
							}}
						>
							{$edit
								? $format('label_finish_reordering', {
										default: 'Finish reordering',
									})
								: $format('label_reorder_items', { default: 'Reorder items' })}
						</Button>
					{/if}
				</div>
			</BreadcrumbsFlexWrapper>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="leftSide">
		{#if type === 'room'}
			<RoomToolbar />
		{/if}

		{#if type === 'scenes'}
			<div class="d-flex align-items-center justify-content-center">
				<Input
					class="width-md-l mr-sm-s text-capitalize-placeholder"
					bind:value={$sceneMainFilter}
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="scenes-toolbar-search-input"
					aria-labelledby="scenes-toolbar-search-input"
				/>
				<IconButton
					icon="fa-solid fa-filter"
					on:click={() => {
						openFilterModal = true;
					}}
					aria-label={$format('aria_open_filter', { default: 'Open filter' })}
					{notification}
				/>
			</div>

			{#if notification && $is_mobile}
				<div class="d-flex align-items-center">
					<IconButton
						on:click={resetData}
						icon="fas fa-xmark"
						variant="secondary"
						class="width-full"
					>
						{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
					</IconButton>
				</div>
			{/if}
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="bottom">
		{#if notification}
			<div class="d-flex align-items-center">
				{#each $roomsScenesFilter.rooms as room}
					<Chip
						aria-label={room.title}
						showChip
						title={room.title}
						class="mr-s flex-shrink-0"
						on:close={resetData}
					/>
				{/each}
				<IconButton
					on:click={resetData}
					icon="fas fa-xmark"
					variant="secondary"
					class="width-full"
				>
					{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
				</IconButton>
			</div>
		{/if}
	</svelte:fragment>
</Toolbar>

<!-- Filter Modal -->
{#if openFilterModal}
	<ScenesFilterModal bind:openFilterModal />
{/if}
